/* noted. — Skipstone allocation builder, footer totals.

   WHAT THIS PUTS ON SCREEN, reading the footer from the right edge inward:

       [ struck retail ]  [ total ]  [ 21 items ]  [ Save ]

   Requested by Justin 2026-08-21: a member customising an allocation could see
   how many items were in the box but not what the box cost.

   MOST OF THE WORK IS IN skipstone-footer-totals.css. Awtomic already ships
   that order and already prints both figures. This file does two small things
   the stylesheet cannot: it clears inline hiding left on those spans by an
   earlier build, and it computes a retail total from the cards for the one
   case where Awtomic prints a single figure and no discount markup.

   ------------------------------------------------------------------
   WHY THIS IS A SEPARATE FILE AND NOT AN EDIT TO skipstone-portal.js.

   That file is 13KB, holds the club minimum guard, and is signed off. The
   guard is the one thing on this page that must not break before the 25th.
   Reopening it to bring two numbers back into view is not a trade worth
   taking, so the hook used here is deliberately small and one-directional.

   HOW IT COEXISTS WITH hideStruckPrices() IN THAT FILE.

   That function hides anything in this footer that is a leaf, holds a $, and
   computes to text-decoration-line: line-through. The retail total is exactly
   that. The stylesheet forces text-decoration off and paints the strike with a
   pseudo-element instead, so the figure reads as struck to a member and as
   unstruck to the guard. Neither file has to know about the other.

   THE TOTAL IS STILL AWTOMIC'S NUMBER. The guard reads the total by scraping
   the footer and taking the LAST money token, and the retail figure always
   sits ahead of the net one. Putting retail last would make the guard compare
   against the wrong number and let an under-minimum allocation save. Do not
   reorder these nodes here or in the stylesheet.

   ------------------------------------------------------------------
   WHAT WAS TRIED AND BACKED OUT, 2026-08-21. DO NOT REDO IT.

   Awtomic leaves no whitespace between the price group and "21 items", so the
   footer's textContent reads "...$3,660.0021 items" and the guard's scrape
   comes back as 3660.0021 rather than 3660.00. The stray digits always land
   past the decimal point, so the error is a fraction of a cent and no minimum
   check has ever turned on it.

   The fix was a zero-sized span holding a single space, inserted as a SIBLING
   of the price group inside .awt-bundle-update-footer-button-info. That is a
   React-managed container, and adding a child to one shifts every index the
   reconciler is holding. The portal came back on a spinner: root marked
   loaded, view never rendered. Reverted immediately.

   The rule this leaves behind: this file may write INSIDE
   .awt-bundle-update-footer-price, which React treats as a leaf here, and may
   not add or remove children anywhere else in Awtomic's tree.

   ------------------------------------------------------------------
   WHY setTimeout AND NOT requestAnimationFrame.

   rAF does not fire in a background tab. A sibling file shipped with an rAF
   debounce and did nothing at all until the tab was looked at, because the
   queued flag latched on and swallowed every mutation behind it. A member
   opening the billing email into a new background tab would have seen the
   same nothing. A timer runs either way.
   ------------------------------------------------------------------

   Applies to all three tiers by construction: nothing here names a tier. */
(function () {
  "use strict";

  if (window.location.pathname.indexOf("/tools/bundle-subscriptions") === -1) return;

  var MONEY = /^\s*\$[\d,]+(\.\d{2})?\s*$/;

  function money(n) {
    return "$" + n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function footer() {
    return document.querySelector(".awt-bundle-update-content-footer") ||
           document.querySelector(".bundleapp-modal__container--complete-screen footer") ||
           document.querySelector(".bundle-container footer");
  }

  /* Sum both columns off the cards. The quantity is in the card's own class as
     q_<n>; the member price is printed plainly with the retail price struck
     beside it when the two differ. Read by shape, never by class name.
     Awtomic's class names are hashed and changed twice in one evening.

     A card with no struck price is at retail, so its two figures are the same
     and it contributes nothing to the gap. */
  function totals() {
    var member = 0, retail = 0;
    var cards = document.querySelectorAll(".bundle-product-card");
    Array.prototype.forEach.call(cards, function (c) {
      var cls = (c.className || "").toString();
      var m = /(?:^|\s)q_(\d+)(?:\s|$)/.exec(cls);
      var q = m ? parseInt(m[1], 10) : 0;
      if (!q) return;

      var net = null, list = null;
      Array.prototype.forEach.call(c.querySelectorAll("*"), function (el) {
        if (el.children.length) return;
        var t = el.textContent;
        if (!MONEY.test(t)) return;
        var v = parseFloat(t.replace(/[^0-9.]/g, ""));
        if (isNaN(v)) return;
        var d = window.getComputedStyle(el).textDecorationLine || "";
        if (d.indexOf("line-through") !== -1) {
          if (list === null) list = v;
        } else if (net === null) {
          net = v;
        }
      });

      if (net === null) return;
      if (list === null) list = net;
      member += net * q;
      retail += list * q;
    });
    return { member: member, retail: retail };
  }

  function paint() {
    var f = footer();
    if (!f) return;
    var wrap = f.querySelector(".awt-bundle-update-footer-price");
    if (!wrap) return;

    wrap.classList.add("skipstone-footer-totals");

    /* Clear the inline hiding an earlier build left on Awtomic's figures.
       React restores className on re-render but leaves inline style alone, so
       this has to be undone by property, not by class. */
    Array.prototype.forEach.call(wrap.querySelectorAll("*"), function (el) {
      if (el.style && el.style.visibility === "hidden") {
        el.style.visibility = "";
        el.style.position = "";
        el.style.pointerEvents = "";
      }
      if (el.classList) el.classList.remove("skipstone-struck-hidden");
    });

    var base = wrap.querySelector(".awt-bundle-update-footer-base-price");
    var discounted = wrap.querySelector(".awt-bundle-update-footer-discounted-price");
    var bundled = wrap.querySelector(".awt-bundle-update-footer-bundle-price");

    /* Awtomic prints both figures whenever a discount applies: base picks up
       bundle-text--strikethrough and the net lands in its own span. When it
       does, theirs wins and ours stays out of the way rather than printing a
       second retail total beside it. */
    var awtomicShowsBoth = !!(base && (
      /bundle-text--strikethrough/.test((base.className || "").toString()) ||
      (discounted && /\$/.test(discounted.textContent)) ||
      (bundled && /\$/.test(bundled.textContent))
    ));

    var host = wrap.querySelector(".skipstone-retail-total");
    if (!host) {
      host = document.createElement("span");
      host.className = "skipstone-retail-total";
      host.appendChild(document.createElement("span")).className = "skipstone-retail-total__v";
    }
    if (host !== wrap.firstChild) wrap.insertBefore(host, wrap.firstChild);

    var t = totals();
    var show = !awtomicShowsBoth && t.retail > t.member + 0.005;
    var text = show ? money(t.retail) : "";
    var leaf = host.firstChild;
    if (leaf.textContent !== text) leaf.textContent = text;
    var want = show ? "" : "none";
    if (host.style.display !== want) host.style.display = want;
  }

  function safePaint() {
    try { paint(); } catch (e) { console.warn("[skipstone-footer-totals]", e); }
  }

  var timer = null;
  function schedule() {
    if (timer) return;
    timer = setTimeout(function () { timer = null; safePaint(); }, 60);
  }

  function start() {
    try {
      safePaint();
      new MutationObserver(schedule).observe(document.body, {
        childList: true, subtree: true, characterData: true
      });
      document.body.addEventListener("click", function () {
        setTimeout(safePaint, 160);
      }, true);
    } catch (e) {
      console.warn("[skipstone-footer-totals] disabled", e);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
